/**
 * The Log form's first field, **Ticket** (ADR 0194 decision 1): where the
 * block's time goes. It replaces the "Add to existing" / "Create a new
 * ticket" switch. The owner: "the ticket can always be a dropdown, and the
 * top item is create new ticket".
 *
 * - **A button** that names the choice: "Create a new ticket", or the
 *   ticket's number and title ("T20261019.0012 · Mailbox move · open"). A
 *   HIPAA client's tickets show their numbers, never their titles.
 * - **Opened, a list in place under it**, with a search on top. The search
 *   is the combobox: ↑ and ↓ move through the list (aria-activedescendant),
 *   Home and End go to its ends, typing narrows it, Enter or a click picks,
 *   and Escape, Tab or a press outside closes it. Enter never sends the
 *   form, which would log.
 * - **The list:** Create a new ticket; then "Open tickets for <client>", each
 *   tagged "continues this work", "logged by you", or its age; then "Use
 *   ticket <typed>" for a number typed that isn't in it.
 *
 * What it picks is the form's to keep: "new", or a ticket as the form sends
 * it, its ID's digits (listed) or its number as typed (ADR 0076).
 */
import { type KeyboardEvent, type ReactNode, useEffect, useEffectEvent, useId, useLayoutEffect, useRef, useState } from "react";

import type { OpenTicket } from "../../api/types";
import { openFor } from "./tickets";

// A ticket's number as Autotask shows it (ADR 0076), as the API checks it.
export const TICKET_NUMBER = /^T\d{8}\.\d{4,}$/i;

/** A ticket the form starts on that may not be in the list: the one the block continues, or was dropped on. */
export interface KnownTicket {
  ticketId: number;
  number: string | null;
}

/** Whether `value` (an ID's digits, or a number as typed) names this ticket. */
function namesTicket(value: string, ticketId: number, number: string | null): boolean {
  return value === String(ticketId) || (number !== null && value.toUpperCase() === number.toUpperCase());
}

interface Option {
  /** "new", a listed ticket's ID's digits, or a typed number. */
  value: string;
  kind: "new" | "ticket" | "use";
  ticket?: OpenTicket;
  tag?: string;
}

function PlusIcon() {
  return (
    <svg
      aria-hidden="true"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      className="mr-1.5 inline-block shrink-0 align-[-2px] text-brand-ink group-data-[active=true]/opt:text-current"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function TicketField({
  clientName,
  isHipaa,
  tickets,
  failed,
  value,
  onPick,
  known,
  mine,
  now,
  timeZone,
  initialOpen = false,
  onOpen,
}: {
  clientName: string;
  isHipaa: boolean;
  /** The client's open tickets, newest first; undefined while they load. */
  tickets: readonly OpenTicket[] | undefined;
  /** They couldn't be loaded: a number can still be typed. */
  failed: boolean;
  /** "new", or the ticket chosen: its ID's digits, or its number. */
  value: string;
  onPick: (value: string) => void;
  /** The tickets it may start on (continued, dropped on), named though not listed; a tag says why. */
  known: readonly (KnownTicket & { tag?: string | undefined })[];
  /** The tickets the tech logged to: "logged by you". */
  mine: ReadonlySet<number>;
  now: Date;
  timeZone: string;
  /** Opened at once, its search focused (a card's "Add to existing ticket…", ADR 0194). */
  initialOpen?: boolean;
  /** Told when it opens: the list is wanted. */
  onOpen?: (() => void) | undefined;
}) {
  const labelId = useId();
  const buttonId = useId();
  const listId = useId();
  const groupId = useId();
  const optionId = useId();
  const [open, setOpen] = useState(initialOpen);
  const [query, setQuery] = useState("");
  // The option the keys and the pointer are on, by its value: it stays on it
  // as the list loads or narrows. None: the first.
  const [activeKey, setActiveKey] = useState<string | null>(initialOpen ? value : null);
  const field = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  const listed = tickets ?? [];
  const tagOf = (ticket: OpenTicket): string => {
    const tag = known.find((k) => k.ticketId === ticket.ticket_id)?.tag;
    if (tag !== undefined) {
      return tag;
    }
    if (mine.has(ticket.ticket_id)) {
      return "logged by you";
    }
    return ticket.created === null ? "open" : openFor(ticket.created, now, timeZone);
  };
  const shownTitle = (ticket: OpenTicket) => (isHipaa ? "" : ticket.title);

  // What the list shows for the search.
  const q = query.trim();
  const lower = q.toLowerCase();
  const hits = lower
    ? listed.filter(
        (t) =>
          (t.number ?? String(t.ticket_id)).toLowerCase().includes(lower) ||
          shownTitle(t).toLowerCase().includes(lower),
      )
    : listed;
  const options: Option[] = [];
  if (!lower || "create a new ticket".includes(lower)) {
    options.push({ value: "new", kind: "new" });
  }
  for (const ticket of hits) {
    options.push({ value: String(ticket.ticket_id), kind: "ticket", ticket, tag: tagOf(ticket) });
  }
  const typed = /^\d+$/.test(q) ? q : TICKET_NUMBER.test(q) ? q.toUpperCase() : null;
  if (
    typed !== null &&
    !listed.some((t) => String(t.ticket_id) === typed || t.number?.toUpperCase() === typed)
  ) {
    options.push({ value: typed, kind: "use" });
  }
  const isChoice = (option: Option, chosen: string | null) =>
    chosen !== null &&
    (option.value === chosen ||
      (option.ticket !== undefined && namesTicket(chosen, option.ticket.ticket_id, option.ticket.number)));
  const found = options.findIndex((o) => isChoice(o, activeKey));
  const activeIndex = options.length === 0 ? -1 : Math.max(found, 0);
  const moveTo = (at: number) => {
    const option = options[at];
    if (option !== undefined) {
      setActiveKey(option.value);
    }
  };

  const close = (focusButton: boolean) => {
    setOpen(false);
    setQuery("");
    if (focusButton) {
      button.current?.focus({ preventScroll: true });
    }
  };
  const start = () => {
    setQuery("");
    setActiveKey(value);
    setOpen(true);
    onOpen?.();
  };
  const pick = (picked: string) => {
    onPick(picked);
    close(true);
  };

  // Started open: the list is wanted.
  const told = useEffectEvent(() => {
    onOpen?.();
  });
  useEffect(() => {
    if (initialOpen) {
      told();
    }
  }, [initialOpen]);
  // Opened: the search takes focus, and the active option is in view.
  useLayoutEffect(() => {
    if (open) {
      search.current?.focus({ preventScroll: true });
    }
  }, [open]);
  useLayoutEffect(() => {
    if (!open || activeIndex < 0) {
      return;
    }
    // Within the list's own scroll, as the prototype keeps it.
    const box = list.current;
    const element = box?.querySelector<HTMLElement>(`[data-index="${String(activeIndex)}"]`);
    if (box === null || element == null) {
      return;
    }
    if (activeIndex === 0) {
      box.scrollTop = 0;
    } else if (element.offsetTop < box.scrollTop) {
      box.scrollTop = element.offsetTop;
    } else if (element.offsetTop + element.offsetHeight > box.scrollTop + box.clientHeight) {
      box.scrollTop = element.offsetTop + element.offsetHeight - box.clientHeight;
    }
  }, [open, activeIndex]);
  // A press outside closes it, and still does what it was aimed at.
  const outside = useEffectEvent((event: PointerEvent) => {
    if (event.target instanceof Node && !field.current?.contains(event.target)) {
      close(false);
    }
  });
  useEffect(() => {
    if (!open) {
      return;
    }
    document.addEventListener("pointerdown", outside, true);
    return () => {
      document.removeEventListener("pointerdown", outside, true);
    };
  }, [open]);

  const onSearchKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (options.length > 0) {
        const by = event.key === "ArrowDown" ? 1 : -1;
        moveTo((activeIndex + by + options.length) % options.length);
      }
    } else if (event.key === "Home" || event.key === "End") {
      if (options.length > 0) {
        event.preventDefault();
        moveTo(event.key === "Home" ? 0 : options.length - 1);
      }
    } else if (event.key === "Enter") {
      // Picks; never sends the form, which would log.
      event.preventDefault();
      const chosen = options[activeIndex];
      if (chosen !== undefined) {
        pick(chosen.value);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      close(true);
    } else if (event.key === "Tab") {
      close(false);
    }
  };

  // What the button says: the choice.
  const chosenTicket = value === "new" ? undefined : listed.find((t) => namesTicket(value, t.ticket_id, t.number));
  const chosenKnown =
    value === "new" || chosenTicket ? undefined : known.find((k) => namesTicket(value, k.ticketId, k.number));
  let shown;
  if (value === "new") {
    shown = (
      <>
        <PlusIcon />
        <span className="font-semibold">Create a new ticket</span>
      </>
    );
  } else if (chosenTicket !== undefined) {
    const title = shownTitle(chosenTicket);
    shown = (
      <>
        <span className="font-semibold">{chosenTicket.number ?? String(chosenTicket.ticket_id)}</span>
        {title ? ` · ${title}` : null}
        <span className="text-muted"> · open</span>
      </>
    );
  } else if (chosenKnown !== undefined) {
    shown = (
      <>
        <span className="font-semibold">{chosenKnown.number ?? String(chosenKnown.ticketId)}</span>
        <span className="text-muted"> · open</span>
      </>
    );
  } else {
    shown = (
      <>
        <span className="font-semibold">{value}</span>
        <span className="text-muted"> · typed in</span>
      </>
    );
  }

  let index = -1;
  const optionRow = (option: Option, inner: ReactNode) => {
    index += 1;
    const at = index;
    const selected = isChoice(option, value);
    return (
      <div
        key={`${option.kind}:${option.value}`}
        id={`${optionId}-${String(at)}`}
        role="option"
        aria-selected={selected}
        data-index={at}
        data-active={at === activeIndex}
        onPointerMove={() => {
          if (at !== activeIndex) {
            setActiveKey(option.value);
          }
        }}
        // A press in the list keeps the focus in the search.
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={() => {
          pick(option.value);
        }}
        className={
          "group/opt flex min-h-[34px] cursor-pointer items-center gap-2 rounded-[8px] py-1 pr-2.5 pl-1.5 text-[13.5px] select-none " +
          "data-[active=true]:bg-brand-tint data-[active=true]:text-brand-ink-on-tint"
        }
      >
        <span aria-hidden="true" className="w-3.5 shrink-0 text-center text-xs text-brand-ink group-data-[active=true]/opt:text-current">
          {selected ? "✓" : ""}
        </span>
        {inner}
      </div>
    );
  };
  const ticketRows = options.filter((o) => o.kind === "ticket");

  return (
    <div ref={field} className="relative flex flex-col gap-[5px]">
      <span id={labelId} className="text-xs font-semibold text-muted">
        Ticket
      </span>
      <button
        ref={button}
        id={buttonId}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-labelledby={`${labelId} ${buttonId}`}
        onClick={() => {
          if (open) {
            close(true);
          } else {
            start();
          }
        }}
        onKeyDown={(event) => {
          if (!open && event.key === "ArrowDown") {
            event.preventDefault();
            start();
          }
        }}
        className={
          "flex h-10 w-full cursor-pointer items-center gap-2 rounded-input border border-line bg-card px-3 text-left text-[13.5px] text-text hover:border-brand " +
          "aria-expanded:border-brand aria-expanded:shadow-[0_0_0_1px_var(--brand)]"
        }
      >
        <span className="min-w-0 flex-1 truncate">{shown}</span>
        <span aria-hidden="true" className="shrink-0 text-[10px] text-muted">
          ▾
        </span>
      </button>
      {open ? (
        <div
          data-part="ticket-list"
          className="absolute top-[calc(100%+4px)] right-0 left-0 z-10 flex flex-col gap-1 rounded-input border border-line bg-panel p-1.5 shadow-[0_2px_6px_rgba(0,0,0,0.06),0_14px_36px_rgba(0,0,0,0.16)] motion-safe:animate-fade-in dark:shadow-[0_2px_6px_rgba(0,0,0,0.4),0_14px_36px_rgba(0,0,0,0.55)]"
        >
          <input
            ref={search}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeIndex < 0 ? undefined : `${optionId}-${String(activeIndex)}`}
            aria-label="Find a ticket or type its number"
            placeholder="Find a ticket or type its number"
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveKey(null);
            }}
            onKeyDown={onSearchKey}
            className="h-[34px] w-full rounded-[9px] border border-line bg-card px-2.5 text-[13px] text-text focus-visible:outline-offset-0"
          />
          <div
            ref={list}
            id={listId}
            role="listbox"
            aria-label="Ticket"
            className="relative flex max-h-[248px] flex-col gap-px overflow-y-auto overscroll-contain"
          >
            {options
              .filter((o) => o.kind === "new")
              .map((o) =>
                optionRow(
                  o,
                  <span className="min-w-0 flex-1 truncate">
                    <PlusIcon />
                    Create a new ticket
                  </span>,
                ),
              )}
            <div role="group" aria-labelledby={groupId}>
              <div
                id={groupId}
                role="presentation"
                className="px-2.5 pt-2 pb-[3px] pl-[26px] text-[11px] font-bold tracking-[0.06em] text-muted uppercase"
              >
                {`Open tickets for ${clientName}`}
              </div>
              {ticketRows.length > 0 ? (
                ticketRows.map((o) => {
                  const ticket = o.ticket as OpenTicket;
                  const title = shownTitle(ticket);
                  return optionRow(
                    o,
                    <>
                      <span className="min-w-0 flex-1 truncate">
                        <b className="font-semibold">{ticket.number ?? String(ticket.ticket_id)}</b>
                        {title ? ` · ${title}` : null}
                      </span>
                      <span className="shrink-0 text-[11.5px] whitespace-nowrap text-muted group-data-[active=true]/opt:text-current">
                        {o.tag}
                      </span>
                    </>,
                  );
                })
              ) : (
                <div role="presentation" className="px-2.5 pt-1 pb-1.5 pl-[26px] text-[12.5px] text-muted">
                  {tickets === undefined && !failed
                    ? "Loading the client's open tickets…"
                    : failed
                      ? "Couldn't load the client's open tickets: type the ticket's number (T20261002.0018)."
                      : listed.length > 0
                        ? "No open ticket matches."
                        : "None open in Autotask."}
                </div>
              )}
            </div>
            {options
              .filter((o) => o.kind === "use")
              .map((o) =>
                optionRow(
                  o,
                  <span className="min-w-0 flex-1 truncate">
                    Use ticket <b className="font-semibold">{o.value}</b>
                  </span>,
                ),
              )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
